import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { Permissions } from '../models/permissions.interface';
import { AuthorizationService } from '../services/authorization-service';
import { filter, map, of, take, tap } from 'rxjs';
@Injectable({
  providedIn: 'root',
})
export class RequestGuard implements CanActivate {

  constructor(
    private router: Router,
    private authorizationService: AuthorizationService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ) {
    const cachedPermissions: Permissions | null = this.getPermissionsFromLocalStorage();
    if (cachedPermissions) {
      return this.evaluateAndMaybeRedirect(cachedPermissions, state.url);
    }

    // Permissions can legitimately be missing right after login/refresh.
    // Trigger a fetch and wait for the first permissions emission instead of redirecting.
    this.authorizationService.fetchPermissions();

    return this.authorizationService.permissions.pipe(
      filter((perms): perms is Permissions => !!perms),
      take(1),
      map((perms) => this.evaluateAndMaybeRedirect(perms, state.url))
    );
  }

  private evaluateAndMaybeRedirect(permissions: Permissions, url: string): boolean {
    const fallbackRoute = this.getFallbackRoute(permissions);

    if (
      url.includes('/tower-requests') &&
      (!permissions?.dataAccessViewing?.canViewRequestPageListingTowers &&
        !permissions?.dataAccessViewing?.canViewApplicationDetailsTowers)
    ) {
      this.router.navigate([fallbackRoute]);
      return false;
    }

    if (
      url.includes('/ibs-requests') &&
      (!permissions?.dataAccessViewing?.canViewRequestPageListingIbs &&
        !permissions?.dataAccessViewing?.canViewApplicationDetailsIbs)
    ) {
      this.router.navigate([fallbackRoute]);
      return false;
    }

    if (url.includes('/contracts')) {
      // The renewed-contract details page is reached from the IBS/Tower
      // request details pages, not the Contracts listing - so it should be
      // reachable under either those pages' view permission or the listing
      // permission, not the listing permission alone.
      const canViewRenewedContractDetails =
        url.includes('/contracts/renew') &&
        !!(permissions?.dataAccessViewing?.canViewApplicationDetailsIbs ||
          permissions?.dataAccessViewing?.canViewApplicationDetailsTowers);

      if (!permissions?.dataAccessViewing?.canViewContractPageListing && !canViewRenewedContractDetails) {
        this.router.navigate([fallbackRoute]);
        return false;
      }
    }

    return true;
  }

  private getFallbackRoute(permissions: Permissions): string {
    const canViewIbs = !!(permissions?.dataAccessViewing?.canViewRequestPageListingIbs || permissions?.dataAccessViewing?.canViewApplicationDetailsIbs);
    if (canViewIbs) {
      return '/ibs-requests';
    }

    const canViewTowers = !!(permissions?.dataAccessViewing?.canViewRequestPageListingTowers || permissions?.dataAccessViewing?.canViewApplicationDetailsTowers);
    if (canViewTowers) {
      return '/tower-requests';
    }

    return '/users';
  }

  // Helper function to retrieve permissions from localStorage
  private getPermissionsFromLocalStorage(): Permissions | null {
    try {
      const permissions = localStorage.getItem('Permissions');
      return permissions ? (JSON.parse(atob(permissions)) as Permissions) : null;
    } catch (error) {
      console.error('Error parsing permissions from localStorage', error);
      return null;
    }
  }
}
